import { collection, deleteDoc, doc, DocumentReference, FirestoreDataConverter, getDoc, setDoc, updateDoc, DocumentData, QueryDocumentSnapshot, SnapshotOptions, getDocs, query, where } from "firebase/firestore";
import { getFirestoreDb } from "@/services/firebase";
import { MigrationManager, MigratableModel } from "@/utils/migrationUtils";
import { IRepo } from "./IRepo";
import { retryFirestoreOperation } from "@/utils/firestoreRetry";

import { FieldValue } from 'firebase/firestore';

type FirestoreScalar = string | number | boolean | null | undefined | Date;

type FirestoreLeafValue =
    | FirestoreScalar
    | FieldValue;

type FirestoreUpdateValue<T> =
    T extends object
    ? FirestoreUpdate<T>
    : FirestoreLeafValue | T;

export type FirestoreUpdate<T> = {
    [K in keyof T]?: FirestoreUpdateValue<T[K]>;
};

export abstract class BaseRepo<T extends MigratableModel> implements IRepo<T> {
    protected migrationManager?: MigrationManager<T>;
    protected collectionName: string = '';

    constructor() {
    }

    protected setupMigrations(migrationManager: MigrationManager<T>): void {
        this.migrationManager = migrationManager;
    }

    protected converter: FirestoreDataConverter<T> = {
        toFirestore: (item: T) => {
            // Ensure schema version is set when writing to Firestore
            const dataToWrite = { ...item };
            if (this.migrationManager) {
                dataToWrite.schemaVersion = this.migrationManager.getCurrentVersion();
            }
            return dataToWrite;
        },
        fromFirestore: (snapshot: QueryDocumentSnapshot<DocumentData>, options?: SnapshotOptions) => {
            const data = snapshot.data(options);
            return data as T;
        }
    };

    /**
     * Process migrations for a document after it's retrieved from Firestore
     */
    protected async processMigrations(data: T, documentId: string): Promise<T> {
        if (!this.migrationManager) {
            return data;
        }

        const migrationResult = await this.migrationManager.migrateDocument(
            data,
            documentId,
            this.collectionName
        );

        // If the document was migrated, save it back to Firestore
        if (migrationResult.migrated) {
            await this.saveMigratedDocument(documentId, migrationResult.data);
        }

        return migrationResult.data;
    }

    /**
     * Get a document by ID
     */
    async get(id: string): Promise<T | null> {
        const docSnap = await getDoc(this.getRef(id));
        if (!docSnap.exists()) {
            return null;
        }
        const data = docSnap.data();
        return data ? this.processMigrations(data, id) : null;
    }

    /**
     * Get a document by a specific property value
     * @param property The property name to query by
     * @param value The value to match
     * @returns The first document that matches the property value, or null if none found
     */
    async getByProperty<K extends keyof T>(property: K, value: T[K]): Promise<T | null> {
        console.log(`doc getByProperty: ${this.collectionName}/${String(property)}/${value}`);
        if (value === undefined || value === null) {
            throw new Error(`${String(property)} value is required`);
        }

        const q = query(
            collection(getFirestoreDb(), this.collectionName),
            where(String(property), '==', value)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        // Get the first document (should be only one if the property is unique)
        const doc = snapshot.docs[0];
        const data = doc.data() as T;

        return data ? this.processMigrations(data, doc.id) : null;
    }

    /**
     * List all documents that match a specific property value
     * @param property The property name to query by
     * @param value The value to match
     * @returns Array of all documents that match the property value
     */
    async listByProperty<K extends keyof T>(property: K, value: T[K]): Promise<T[]> {
        console.log(`doc listByProperty: ${this.collectionName}/${String(property)}/${value}`);
        if (value === undefined || value === null) {
            throw new Error(`${String(property)} value is required`);
        }

        const q = query(
            collection(getFirestoreDb(), this.collectionName),
            where(String(property), '==', value)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return [];
        }

        // Process all matching documents with migrations
        const promises = snapshot.docs.map(async (doc) => {
            const data = doc.data() as T;
            if (!data) return null;
            try {
                return await this.processMigrations(data, doc.id);
            } catch (error) {
                console.error(`Error processing migrations for document ${doc.id}:`, error);
                return null;
            }
        });

        const results = await Promise.all(promises);
        return results.filter((item): item is NonNullable<typeof item> => item !== null);
    }

    /**
     * List all documents in the collection
     * @returns Array of all documents in the collection
     */
    async listAll(): Promise<T[]> {
        console.log(`doc listAll: ${this.collectionName}`);
        const q = query(collection(getFirestoreDb(), this.collectionName));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return [];
        }

        const promises = querySnapshot.docs.map(async (doc) => {
            const data = doc.data() as T;
            if (!data) return null;
            try {
                return await this.processMigrations(data, doc.id);
            } catch (error) {
                console.error(`Error processing migrations for document ${doc.id}:`, error);
                return null;
            }
        });

        const results = await Promise.all(promises);
        return results.filter((item): item is NonNullable<typeof item> => item !== null);
    }

    /**
     * Find documents by multiple query conditions
     * @param conditions Array of [field, operator, value] tuples
     * @returns Array of documents matching all conditions
     */
    async findByQuery(conditions: [string, string, any][]): Promise<T[]> {
        console.log(`doc findByQuery: ${this.collectionName}`, conditions);

        if (conditions.length === 0) {
            return this.listAll();
        }

        let q = query(collection(getFirestoreDb(), this.collectionName));

        for (const [field, operator, value] of conditions) {
            q = query(q, where(field, operator as any, value));
        }

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return [];
        }

        const promises = snapshot.docs.map(async (doc) => {
            const data = doc.data() as T;
            if (!data) return null;
            try {
                return await this.processMigrations(data, doc.id);
            } catch (error) {
                console.error(`Error processing migrations for document ${doc.id}:`, error);
                return null;
            }
        });

        const results = await Promise.all(promises);
        return results.filter((item): item is NonNullable<typeof item> => item !== null);
    }

    /**
     * Save a migrated document back to Firestore
     */
    protected async saveMigratedDocument(id: string, data: T): Promise<void> {
        console.log('Saving migrated document', id, data);
        await retryFirestoreOperation(async () => {
            await setDoc(this.getRef(id), this.converter.toFirestore(data));
        }, {
            maxRetries: 3,
            initialDelay: 1000,
            maxDelay: 5000
        });
    }

    /** Gets the document reference for the given id from the server. */
    private getRef(id: string): DocumentReference<T> {
        return doc(getFirestoreDb(), this.collectionName, id).withConverter(this.converter);
    }

    /** Gets the item from the server and applies any necessary migrations. */
    async getById(id: string): Promise<T | null> {
        console.log(`doc getById: ${this.collectionName}/${id}`);
        if (!id) {
            throw new Error('id is required');
        }
        const snapshot = await getDoc(this.getRef(id));
        if (!snapshot.exists()) {
            return null;
        }
        const data = snapshot.data();
        return data ? this.processMigrations(data, id) : null;
    }

    /** Creates a new id for the item on the server using Firestore's auto-generated id. */
    async createFirestoreId(): Promise<string> {
        const newRef = doc(collection(getFirestoreDb(), this.collectionName));
        return newRef.id;
    }

    /** Creates the item on the server and the cache. */
    async create(item: T, id: string): Promise<void> {
        await retryFirestoreOperation(async () => {
            await setDoc(this.getRef(id), item);
        }, {
            maxRetries: 3,
            initialDelay: 1000,
            maxDelay: 5000
        });
    }

    /** Updates the item on the server. For partial updates, ensures the existing data is migrated first. */
    async update(id: string, updates: FirestoreUpdate<T>): Promise<void> {
        await retryFirestoreOperation(async () => {
            const docRef = this.getRef(id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                // For partial updates, we need to migrate the existing data first
                const existingData = docSnap.data();
                if (existingData) {
                    const migratedData = await this.processMigrations(existingData, id);
                    // Merge the updates with the migrated data
                    const mergedData = { ...migratedData, ...updates };
                    await updateDoc(docRef, this.converter.toFirestore(mergedData));
                } else {
                    await updateDoc(docRef, updates as any);
                }
            } else {
                // For new documents, just set the updates
                await setDoc(docRef, updates as any);
            }
        }, {
            maxRetries: 3,
            initialDelay: 1000,
            maxDelay: 5000
        });
    }

    /** Deletes the item from the server and the cache. */
    async delete(id: string): Promise<void> {
        await retryFirestoreOperation(async () => {
            await deleteDoc(this.getRef(id));
        }, {
            maxRetries: 3,
            initialDelay: 1000,
            maxDelay: 5000
        });
    }

    /**
     * Get migration information for this repository
     */
    getMigrationInfo(): { currentVersion: number; migrations: any[] } | null {
        if (!this.migrationManager) {
            return null;
        }

        return {
            currentVersion: this.migrationManager.getCurrentVersion(),
            migrations: this.migrationManager.getMigrations()
        };
    }
}